import PropTypes from 'prop-types';
import * as Yup from 'yup';
import merge from 'lodash/merge';
import { isBefore } from 'date-fns';
import { useSnackbar } from 'notistack';
// form
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Box, Stack, Button, TextField } from '@mui/material';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers';
import { LoadingButton } from '@mui/lab';
// redux
// import { useDispatch } from '../../../redux/store';
// import { createEvent, updateEvent, deleteEvent } from '../../../redux/slices/calendar';
// components
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSelector } from 'react-redux';
import { capturePHEvents } from '../../../analytics/posthog.analytics';
import { PH_EVENT_UPDATED, PH_NEW_EVENT_ADDED } from '../../../utils/posthog.eventNames';
import { ColorSinglePicker } from '../../../fl-components/color-utils';
import { FormProvider, RHFTextField, RHFRadioGroup } from '../../../fl-components/hook-form';
import {
  createNewCalenderEvent,
  deleteCalenderEvent,
  updateCalenderEvent
} from '../../../service/calenderEvent.service';

// ----------------------------------------------------------------------

const COLOR_OPTIONS = [
  '#00AB55', // theme.palette.primary.main,
  '#1890FF', // theme.palette.info.main,
  '#54D62C', // theme.palette.success.main,
  '#FFC107', // theme.palette.warning.main,
  '#FF4842', // theme.palette.error.main
  '#04297A', // theme.palette.info.darker
  '#7A0C2E' // theme.palette.error.darker
];

const getInitialValues = (event, range) => {
  const _event = {
    title: '',
    description: '',
    textColor: '#1890FF',
    // allDay: false,
    type: 'event',
    start: range ? new Date(range.start) : new Date(),
    end: range ? new Date(range.end) : new Date()
  };

  if (event || range) {
    return merge({}, _event, event);
  }

  return _event;
};

// ----------------------------------------------------------------------

export default function CalendarForm({
  event,
  range,
  onCancel,
  onAddSuccess,
  onUpdateSuccess,
  onDeleteSuccess,
  isDeleteLoading,
  setEventSubmitting,
  setDeleteEventLoading,
  isEventSubmitting
}) {
  const selectedOrg = useSelector((state) => state.org.selectedOrg);
  const currentUser = useSelector((state) => state.auth.currentUser);
  const { enqueueSnackbar } = useSnackbar();

  const isCreating = Object.keys(event).length === 0;

  const EventSchema = Yup.object().shape({
    title: Yup.string().max(255).required('Title is required'),
    description: Yup.string().max(5000)
  });

  const methods = useForm({
    resolver: yupResolver(EventSchema),
    defaultValues: getInitialValues(event, range)
  });

  const { reset, watch, control, handleSubmit } = methods;

  const onSubmit = async (data) => {
    try {
      setEventSubmitting(true);
      const newEvent = {
        title: data.title,
        // description: data.description,
        textColor: data.textColor,
        type: data.type,
        // date: data.start,
        startDate: data.start,
        endDate: data.end,
        orgId: selectedOrg?._id ?? selectedOrg?.id ?? selectedOrg?.orgId,
        userId: currentUser.id
      };
      if (data.description) newEvent.description = data.description;
      if (event.id) {
        const res = await updateCalenderEvent(event.id, newEvent);
        if (res) {
          capturePHEvents(PH_EVENT_UPDATED, { orgId: newEvent.orgId, eventId: event.id });
          onUpdateSuccess({ ...res, start: res.startDate, end: res.endDate });
          enqueueSnackbar('Update success!');
        }
      } else {
        const res = await createNewCalenderEvent(newEvent);
        if (res) {
          capturePHEvents(PH_NEW_EVENT_ADDED, { orgId: newEvent.orgId, eventId: res.id });
          onAddSuccess({ ...res, start: res.startDate, end: res.endDate });
          enqueueSnackbar(`${data.type} created success!`);
        }
      }
      setEventSubmitting(false);
      onCancel();
      reset();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async () => {
    if (!event.id) return;
    try {
      setDeleteEventLoading(true);
      await deleteCalenderEvent(event.id);
      onDeleteSuccess(event.id);
      enqueueSnackbar('Delete success!');
      setDeleteEventLoading(false);
      onCancel();
    } catch (error) {
      console.error(error);
    }
  };

  const values = watch();

  const isDateError = isBefore(new Date(values.end), new Date(values.start));

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <RHFTextField name="title" label="Title" />

        <RHFTextField name="description" label="Description" multiline rows={4} />

        {/* <RHFSwitch name="allDay" label="All day" /> */}
        <RHFRadioGroup
          name="type"
          options={[
            { value: 'event', label: 'Event' },
            { value: 'holiday', label: 'Holiday' }
          ]}
        />

        <Controller
          name="start"
          control={control}
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <MobileDateTimePicker
                {...field}
                label="Start date"
                inputFormat="dd/MM/yyyy hh:mm a"
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          )}
        />

        <Controller
          name="end"
          control={control}
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <MobileDateTimePicker
                {...field}
                label="End date"
                inputFormat="dd/MM/yyyy hh:mm a"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={!!isDateError}
                    helperText={isDateError && 'End date must be later than start date'}
                  />
                )}
              />
            </LocalizationProvider>
          )}
        />

        <Controller
          name="textColor"
          control={control}
          render={({ field }) => (
            <ColorSinglePicker
              value={field.value}
              onChange={field.onChange}
              colors={COLOR_OPTIONS}
            />
          )}
        />
      </Stack>

      {!isCreating && (
        <Box textAlign="center" mt={2}>
          <Button
            disabled={isDeleteLoading || isEventSubmitting}
            variant="contained"
            color="error"
            onClick={() => handleDelete()}
          >
            Delete Event
          </Button>
        </Box>
      )}

      <LoadingButton
        sx={{ display: 'none' }}
        id="eventSubmitButton"
        type="submit"
        variant="contained"
      >
        Add
      </LoadingButton>
    </FormProvider>
  );
}

CalendarForm.propTypes = {
  event: PropTypes.object,
  range: PropTypes.object,
  onCancel: PropTypes.func,
  onAddSuccess: PropTypes.func,
  onUpdateSuccess: PropTypes.func,
  onDeleteSuccess: PropTypes.func,
  isDeleteLoading: PropTypes.bool,
  setEventSubmitting: PropTypes.func,
  setDeleteEventLoading: PropTypes.func,
  isEventSubmitting: PropTypes.bool
};
